import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["file"];
import { useEffect, useState } from 'react';
import PortalIdParser from 'PortalIdParser';
import { TypeToExtensions } from '../constants/fileTypes';
import { BYTES_IN_MEGABYTE, FileTypes, ThumnailSizeToIconSize } from '../constants';
import { getThumbnailUrl } from '../utils/thumbnails';
import VideoIconOverlay from '../image/VideoIconOverlay';
import styled from 'styled-components';
import FileTypeIcon, { LocalFileIcon } from './FileTypeIcon';
import { canPreviewLocalFile, isShutterstockImage } from '../utils/file';
import { getFileTypeFromFile } from '../utils/fileType';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ThumbnailContainerStyled = styled.div.withConfig({
  displayName: "ThumbnailPreviewOrIcon__ThumbnailContainerStyled",
  componentId: "sc-1i3vbr1-0"
})(["width:100%;height:100%;position:relative;display:flex;align-items:center;justify-content:center;"]);
const ImgStyled = styled.img.withConfig({
  displayName: "ThumbnailPreviewOrIcon__ImgStyled",
  componentId: "sc-1i3vbr1-1"
})(["max-width:100%;max-height:", ";pointer-events:", ";"], props => props.maxHeight ? `${props.maxHeight}px` : '100%', props => props.isShutterstockImage ? 'none' : 'auto');
const FORM_UPLOADS_FOLDER_NAME = 'form-uploads';
const getIsFileSubmittedThroughForms = url => {
  const fileUrl = url || '';
  return fileUrl.indexOf(`hubfs/${PortalIdParser.get()}/${FORM_UPLOADS_FOLDER_NAME}`) > -1;
};
const getIsFileAccessibleAnonymously = meta => {
  return meta.allows_anonymous_access;
};
const getIsFileExternal = (meta, url) => {
  return !getIsFileAccessibleAnonymously(meta) && getIsFileSubmittedThroughForms(url);
};
const getType = extensionOrType => {
  if (Object.prototype.hasOwnProperty.call(FileTypes, extensionOrType)) {
    return extensionOrType;
  }
  const type = Object.keys(TypeToExtensions).find(key => TypeToExtensions[key].includes(extensionOrType));
  if (type) {
    return type;
  }
  return FileTypes.OTHER;
};
const getFileType = (type, extension) => {
  if (type) {
    return type;
  }
  return getType(extension);
};
const getIsImage = (type, extension) => {
  return getFileType(type, extension) === FileTypes.IMG;
};
const getIsSvg = (type, extension, encoding) => {
  return getIsImage(type, extension) && encoding === 'svg';
};
const getIsSvgTooLarge = size => {
  return size >= BYTES_IN_MEGABYTE;
};
const getIsTemporaryUploadingFile = id => {
  return id <= 0;
};
const getHasThumbnail = (type, extension, encoding, size, id, meta, url) => {
  if (getIsSvg(type, extension, encoding) && (getIsSvgTooLarge(size) || getIsTemporaryUploadingFile(id))) {
    return false;
  }
  if (getIsFileExternal(meta, url)) {
    return false;
  }
  const fileType = getFileType(type, extension);
  return fileType === FileTypes.IMG || fileType === FileTypes.MOVIE;
};
const getIsFilePrivate = (meta, url) => {
  return !getIsFileAccessibleAnonymously(meta) && !getIsFileSubmittedThroughForms(url);
};
const ThumbnailPreviewOrIconForFMFile = ({
  file,
  maxHeight,
  thumbnailSize
}) => {
  const {
    type,
    extension,
    encoding,
    size,
    id,
    meta,
    url
  } = file;
  const [errorLoadingThumbnail, setErrorLoadingThumbnail] = useState(false);
  const fileType = getFileType(type, extension);
  if (!getHasThumbnail(type, extension, encoding, size, id, meta, url) || errorLoadingThumbnail) {
    return /*#__PURE__*/_jsx(FileTypeIcon, {
      fileType: fileType,
      extension: extension,
      size: thumbnailSize ? ThumnailSizeToIconSize[thumbnailSize] : undefined
    });
  }
  const src = getThumbnailUrl(id, thumbnailSize, getIsFilePrivate(meta, url));
  return /*#__PURE__*/_jsxs(ThumbnailContainerStyled, {
    children: [fileType === FileTypes.MOVIE && /*#__PURE__*/_jsx(VideoIconOverlay, {}), /*#__PURE__*/_jsx(ImgStyled, {
      draggable: false,
      maxHeight: maxHeight,
      isShutterstockImage: isShutterstockImage(file),
      src: src,
      onError: () => {
        setErrorLoadingThumbnail(true);
        console.warn(`Thumbnail url ${src} failed to load, falling back to generic FileTypeIcon`);
      }
    })]
  });
};
const ThumbnailPreviewOrIconForLocalFile = ({
  file,
  maxHeight,
  thumbnailSize
}) => {
  const [tempUrl, setTempUrl] = useState();
  useEffect(() => {
    if (canPreviewLocalFile(file) && !tempUrl) {
      setTempUrl(window.URL.createObjectURL(file));
    }
    return () => {
      if (tempUrl) {
        window.URL.revokeObjectURL(tempUrl);
      }
    };
  }, [file, tempUrl]);
  const fileType = getFileTypeFromFile(file);
  if (!tempUrl) {
    return /*#__PURE__*/_jsx(LocalFileIcon, {
      file: file,
      size: thumbnailSize ? ThumnailSizeToIconSize[thumbnailSize] : undefined
    });
  }
  return /*#__PURE__*/_jsxs(ThumbnailContainerStyled, {
    children: [fileType === FileTypes.MOVIE && /*#__PURE__*/_jsx(VideoIconOverlay, {}), /*#__PURE__*/_jsx(ImgStyled, {
      src: tempUrl,
      draggable: false,
      maxHeight: maxHeight
    })]
  });
};
const ThumbnailPreviewOrIcon = _ref => {
  let {
      file
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (file instanceof File) {
    return /*#__PURE__*/_jsx(ThumbnailPreviewOrIconForLocalFile, Object.assign({
      file: file
    }, props));
  }
  return /*#__PURE__*/_jsx(ThumbnailPreviewOrIconForFMFile, Object.assign({
    file: file
  }, props));
};
export default ThumbnailPreviewOrIcon;